import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';

const useStyles = makeStyles((theme) => ({
  speechBubble: {
    position: 'relative',
    padding: 16,
    '-webkit-border-radius': 8,
    '-moz-border-radius': 8,
  },
  rightSpeechBubble: {
    borderRadius: '32px 32px 0px 32px',
    marginLeft: '40%',
    marginRight: 0,
    background: theme.palette.common.black,
    opacity: 0.95,
    color: theme.palette.common.white,
  },
  leftSpeechBubble: {
    borderRadius: '0px 32px 32px 32px',
    marginLeft: 0,
    marginRight: '40%',
    background: theme.palette.common.black,
    opacity: 0.95,
    color: theme.palette.common.white,
  },
  contentContainer: {
    marginRight: 32,
  },
  contentHeader: {
    minHeight: 30,
  },
  userName: {
    height: 30,
    lineHeight: '30px',
  },
}));

const StyledAvatar = withStyles(() => ({
  root: {
    height: 60,
    width: 60,
    marginRight: 16,
  },
}))(Avatar);

/**
 * SpeechBubble
 *
 * Single speech bubble that represents a message.
 * It was built to be used inside the `Conversations` component.
 *
 * @param text
 * @param isCurrentUser
 * @param user
 * @param created
 */
const SpeechBubble = ({ text, isCurrentUser = false, user, created, ...other }) => {
  SpeechBubble.propTypes = {
    /**
     * Message text
     */
    text: PropTypes.string,
    /**
     * Boolean value to control the message position.
     * If `true`, the message will be on the right.
     * If `false`, the message will be on the left.
     */
    isCurrentUser: PropTypes.bool,
    /**
     * User who sent the message.
     * Even when there are "no users" you should still provide something for identification.
     * You can pass `{ name: 'Anonymous' }` for example.
     */
    user: PropTypes.shape({
      name: PropTypes.string.isRequired,
      avatarURL: PropTypes.string,
    }),
    /**
     * Date of when the message was created.
     * No specific format.
     */
    created: PropTypes.string.isRequired,
    /**
     * Props that you would like to pass to the main element/container.
     */
    other: PropTypes.shape({}),
  };

  const classes = useStyles();

  return (
    <div
      className={classNames(
        classes.speechBubble,
        { [classes.rightSpeechBubble]: isCurrentUser },
        { [classes.leftSpeechBubble]: !isCurrentUser },
      )}
      {...other}
    >
      <Grid container data-testid="chat-bubble">
        <StyledAvatar data-testid="chat-avatar" variant="rounded" alt={user.name} src={user.avatarURL} />
        <Grid container item xs className={classes.contentContainer}>
          <Grid container justifyContent={'space-between'} className={classes.contentHeader}>
            <Typography variant={'body1'} data-testid="chat-name">
              {user.name}
            </Typography>
            <Typography variant={'body2'} data-testid="chat-date">
              {created}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant={'body2'} data-testid="chat-content">
              {text}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default SpeechBubble;
